/**
 * useTour Hook
 *
 * Hook for managing First Time Wizard tour state and API synchronization.
 * Uses React Query for caching and deduplication.
 *
 * Key behaviors:
 * - Fetches tour status on mount
 * - Shows tour if not previously dismissed (fail-open on API errors)
 * - Tracks step navigation locally (no per-step backend calls)
 * - Sends single backend update on close/finish with accumulated state
 * - Provides TourComponent to render Ant Design Tour
 *
 * @example
 * const { TourComponent, isOpen, replay } = useTour(workflowDataTour)
 *
 * return (
 *   <>
 *     <PageContent />
 *     <TourComponent />
 *     <Button onClick={replay}>Replay Tour</Button>
 *   </>
 * )
 */

import { useState, useCallback, useMemo, useEffect, useRef, FC } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Tour, Checkbox, Space } from 'antd'
import type { TourProps } from 'antd'
import { tourService } from '../services/tourService'
import type {
  TourDefinition,
  TourStatus,
  TourStatusUpdate,
  UseTourResult
} from '../types/tour.types'

/**
 * React Query configuration for tour status
 *
 * - staleTime: 1 hour (dismissal status rarely changes)
 * - gcTime: 24 hours (keep in cache while user is active)
 * - refetchOnWindowFocus: false (no need to refetch on tab switch)
 * - retry: false (fail-open - show tour if API fails)
 */
const QUERY_CONFIG = {
  staleTime: 1000 * 60 * 60,      // 1 hour
  gcTime: 1000 * 60 * 60 * 24,    // 24 hours
  refetchOnWindowFocus: false,
  retry: false,                    // Don't retry - show tour on any error
}

/**
 * Default tour status for never-viewed tours or API failures
 */
const DEFAULT_STATUS: TourStatus = {
  tourKey: '',
  dismissed: false,
  viewCount: 0,
  maxStepReached: 0,
  totalSteps: 0,
  firstViewedAt: null,
  dismissedAt: null,
}

/**
 * Hook for managing tour state and API synchronization.
 *
 * @param definition - Tour definition with steps and metadata
 * @returns Tour control functions and state
 */
export function useTour(definition: TourDefinition): UseTourResult {
  const queryClient = useQueryClient()
  const { tourKey, steps } = definition

  // Local UI state
  const [isOpen, setIsOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [hasShownTour, setHasShownTour] = useState(false)
  const [dontShowAgain, setDontShowAgain] = useState(false)

  // Track highest step reached locally (1-indexed) - sent to backend on close
  const maxStepReachedRef = useRef(0)

  // Guard against double close calls (Ant Design fires both onFinish and onClose)
  const isClosingRef = useRef(false)

  // Query key for this tour's status
  const queryKey = ['tour-status', tourKey]

  // Fetch tour status from API
  const {
    data: statusResponse,
    isLoading,
    isError,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await tourService.getStatus(tourKey)
      if (response.success && response.data) {
        return response.data
      }
      // Fail-open: return default status on error (will show tour)
      console.warn(`[useTour] Failed to fetch status for ${tourKey}, using defaults`)
      return { ...DEFAULT_STATUS, tourKey }
    },
    ...QUERY_CONFIG,
  })

  // Extract status with fallback to defaults
  const status = statusResponse || { ...DEFAULT_STATUS, tourKey }

  // Mutation for updating tour status - called once on tour close/finish
  const updateMutation = useMutation({
    mutationFn: async (data: TourStatusUpdate) => {
      const response = await tourService.updateStatus(tourKey, data)
      if (response.success && response.data) {
        return response.data
      }
      throw new Error(response.errors?.[0]?.message || 'Failed to update tour status')
    },
    onSuccess: (data) => {
      // Always update cache - single call on close contains final state
      queryClient.setQueryData(queryKey, data)
    },
    onError: (error) => {
      // Log but don't interrupt user experience
      console.error(`[useTour] Failed to update status for ${tourKey}:`, error)
    },
  })

  // Ref to hold stable reference to mutate function
  // This prevents TourComponent from re-rendering when mutation state changes
  const mutateRef = useRef(updateMutation.mutate)
  mutateRef.current = updateMutation.mutate

  // Show tour on first mount if not dismissed
  // Uses fail-open strategy: show tour if loading fails or status indicates not dismissed
  // Backend is NOT called here - single update sent on close/finish
  useEffect(() => {
    if (hasShownTour) return // Only auto-show once
    if (isLoading) return // Wait for status to load

    // Show tour if not dismissed (or on error - fail-open)
    const shouldShow = isError || !status.dismissed
    if (shouldShow) {
      setIsOpen(true)
      setHasShownTour(true)
      maxStepReachedRef.current = 1
      isClosingRef.current = false
    }
  }, [isLoading, isError, status.dismissed, hasShownTour])

  /**
   * Close tour (may or may not dismiss permanently based on checkbox)
   * Called when user clicks X or Done button.
   * This is the ONLY place that sends a backend update - single call with
   * accumulated state (maxStepReached, totalSteps, dismissed).
   *
   * Guard: Ant Design fires both onFinish and onClose when clicking Finish,
   * so we use isClosingRef to prevent duplicate backend calls.
   */
  const closeTour = useCallback((shouldDismiss: boolean) => {
    if (isClosingRef.current) return
    isClosingRef.current = true

    setIsOpen(false)

    // Ensure maxStepReached includes the current step
    const finalMaxStep = Math.max(maxStepReachedRef.current, currentStep + 1)

    // Single backend call with final accumulated state
    mutateRef.current({
      maxStepReached: finalMaxStep,
      totalSteps: steps.length,
      dismissed: shouldDismiss,
    })

    // Reset checkbox for next time
    setDontShowAgain(false)
  }, [currentStep, steps.length])

  /**
   * Advance to next step
   */
  const next = useCallback(() => {
    const nextStep = currentStep + 1
    if (nextStep >= steps.length) {
      // Last step - close and dismiss (backend call happens in closeTour)
      maxStepReachedRef.current = Math.max(maxStepReachedRef.current, steps.length)
      closeTour(true)
    } else {
      setCurrentStep(nextStep)
      maxStepReachedRef.current = Math.max(maxStepReachedRef.current, nextStep + 1)
    }
  }, [currentStep, steps.length, closeTour])

  /**
   * Go to previous step
   */
  const prev = useCallback(() => {
    const prevStep = Math.max(0, currentStep - 1)
    setCurrentStep(prevStep)
    // No need to update maxStepReached when going back
  }, [currentStep])

  /**
   * Permanently dismiss tour (legacy - for programmatic dismissal)
   */
  const dismiss = useCallback(() => {
    closeTour(true)
  }, [closeTour])

  /**
   * Replay tour (for "Replay tour" link)
   * Opens tour from beginning regardless of previous dismissal.
   * Backend update happens when the replayed tour closes.
   */
  const replay = useCallback(() => {
    setCurrentStep(0)
    maxStepReachedRef.current = 1
    isClosingRef.current = false
    setIsOpen(true)
  }, [])

  /**
   * Build Ant Design Tour steps from definition
   *
   * Note: We cast the target function because our TourStepDefinition
   * allows target to return null, but Ant Design's type is more strict.
   * At runtime, Ant Design handles null targets gracefully.
   */
  const antSteps: TourProps['steps'] = useMemo(() => {
    return steps.map((step) => ({
      title: step.title,
      description: step.description,
      cover: step.cover,
      // Cast to satisfy Ant Design's stricter types - null targets work at runtime
      target: step.target as (() => HTMLElement) | undefined,
      placement: step.placement,
    }))
  }, [steps])

  // Refs for stable access from memoized TourComponent
  const closeTourRef = useRef(closeTour)
  closeTourRef.current = closeTour
  const dontShowAgainRef = useRef(dontShowAgain)
  dontShowAgainRef.current = dontShowAgain

  /**
   * Tour component that wraps Ant Design Tour.
   * Uses useMemo to create a proper React component (useCallback returns a
   * memoized function without React's internal fiber queue, causing
   * "Should have a queue" errors on re-render).
   */
  const TourComponent: FC = useMemo(() => {
    const TourRenderer: FC = () => (
      <>
        <style>{`
          .rp-tour .ant-tour-inner {
            border: 2px solid #1890ff;
            box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.15), 0 3px 6px -4px rgba(0, 0, 0, 0.12);
          }
        `}</style>
        <Tour
          open={isOpen}
          current={currentStep}
          steps={antSteps}
          rootClassName="rp-tour"
          onClose={() => closeTourRef.current(dontShowAgainRef.current)}
          onChange={(step) => {
            setCurrentStep(step)
            // Track highest step locally - backend update happens on close
            maxStepReachedRef.current = Math.max(maxStepReachedRef.current, step + 1)
          }}
          onFinish={() => closeTourRef.current(dontShowAgainRef.current)}
          indicatorsRender={(current, total) => (
            <Space size="middle">
              <span>{current + 1} / {total}</span>
              <Checkbox
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
              >
                Don't show this again
              </Checkbox>
            </Space>
          )}
        />
      </>
    )
    TourRenderer.displayName = 'TourRenderer'
    return TourRenderer
  }, [isOpen, currentStep, antSteps, closeTour, dontShowAgain])

  return {
    isOpen,
    currentStep,
    isLoading,
    isError,
    next,
    prev,
    dismiss,
    replay,
    TourComponent,
  }
}

export default useTour
